import axios, {AxiosInstance} from "axios";
import {ElNotification} from "element-plus";
import {useAuthStore} from '@/stores';
import router from '@/router';
import {getSessionMeta, getChunkCount} from '@/utils/recorderStore';

const apiClient: AxiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_WRAP_URL,
    timeout: 300000, // request timeout
});

// Add request interceptor to set headers dynamically
apiClient.interceptors.request.use(
    (config) => {
        const { accessToken } = useAuthStore();
        if (accessToken) {
            config.headers.Authorization = `${accessToken}`;
        }
        const shareToken = sessionStorage.getItem('share_token');
        if (shareToken) {
            config.headers['X-Share-Token'] = shareToken;
        }
        // Chỉ set Content-Type khi request THỰC SỰ có body.
        // GET/HEAD không có body: thêm Content-Type sẽ khiến trình duyệt coi đây là
        // request "không đơn giản" và phải gửi preflight OPTIONS trước → mỗi lần gọi
        // mất 2 lượt mạng, tăng gấp đôi khả năng lỗi khi mạng chập chờn.
        const hasBody = config.data !== undefined && config.data !== null;
        if (hasBody && !config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Xử lý khi phiên đăng nhập hết hạn.
 *
 * Có cờ chống trùng: nhiều request có thể cùng thất bại một lúc (trang chi tiết
 * gọi 3-4 API song song) — nếu không chặn thì người dùng nhận vài thông báo giống
 * nhau và bị điều hướng nhiều lần.
 *
 * Thông báo được hiện TRƯỚC, chuyển trang sau một nhịp để người dùng kịp đọc;
 * thông báo vẫn nằm lại trên trang đăng nhập nên chắc chắn không bị bỏ lỡ.
 */
let isHandlingSessionExpired = false;

/**
 * Handle a TOKEN_INVALID response.
 *
 * When the user has an unsaved recording in IndexedDB (chunks > 0) we do NOT
 * log them out or redirect immediately — losing a 2–3 h recording is a much
 * worse outcome than staying on the page with an expired session.  Instead we
 * show a persistent warning that tells them to log in again and retry.
 *
 * In all other cases (normal navigation, no recording in progress) we fall back
 * to the original behaviour: log out and redirect to the login page.
 */
const handleSessionExpired = async () => {
    if (isHandlingSessionExpired) return;
    isHandlingSessionExpired = true;

    try {
        // Check whether there is a pending recording the user could lose.
        const meta = await getSessionMeta();
        const chunkCount = meta ? await getChunkCount() : 0;
        const hasActiveRecording = chunkCount > 0;

        if (hasActiveRecording) {
            // Bản ghi còn trong IndexedDB — không đăng xuất, không redirect.
            // Chỉ cảnh báo inline để người dùng đăng nhập lại rồi thử tải lên.
            ElNotification({
                title: 'Phiên đăng nhập đã hết hạn',
                message:
                    'Bản ghi của bạn vẫn an toàn trên máy. Vui lòng đăng nhập lại và thử tải lên.',
                type: 'warning',
                duration: 0,
                showClose: true,
            });
            isHandlingSessionExpired = false;
            return;
        }
    } catch {
        // If we cannot read IndexedDB, fall through to the normal logout path.
    }

    const authStore = useAuthStore();
    authStore.logout();

    ElNotification({
        title: 'Phiên đăng nhập đã hết hạn',
        message:
            'Bạn không hoạt động trong thời gian dài nên hệ thống đã tự đăng xuất để bảo vệ tài khoản. Vui lòng đăng nhập lại để tiếp tục.',
        type: 'warning',
        duration: 8000,
        showClose: true,
    });

    // Chờ một nhịp để người dùng đọc thông báo rồi mới rời trang
    setTimeout(() => {
        if (router.currentRoute.value.name !== 'LoginSignUp') {
            router.push({name: 'LoginSignUp'}).catch(() => { /* điều hướng trùng, bỏ qua */ });
        }
        isHandlingSessionExpired = false;
    }, 2000);
};

apiClient.interceptors.response.use(
    (res) => {
        // Check for TOKEN_INVALID in the response
        if (res.data?.code === 'TOKEN_INVALID') {
            const shareToken = sessionStorage.getItem('share_token');
            if (shareToken) {
                ElNotification({
                    title: 'Lỗi',
                    message: 'Link chia sẻ không hợp lệ hoặc đã hết hạn',
                    type: 'error',
                });
                return Promise.reject(new Error('SHARE_TOKEN_INVALID'));
            } else {
                handleSessionExpired();
                return Promise.reject(new Error('TOKEN_INVALID'));
            }
        }
        return res.data;
    },
    (err) => {
        const status = err?.response?.status;
        let message =
            err?.response?.data?.statusText ||
            err?.response?.data?.message ||
            err?.response?.data?.detail?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            "Đã xảy ra lỗi";

        if (err.code === "ECONNABORTED") {
            message = "Không nhận được phản hồi từ server";
        } else if (!err.response) {
            // Không có response: mất mạng, DNS, CORS bị chặn, server không tới được
            message = "Không kết nối được tới server. Kiểm tra đường mạng rồi thử lại.";
        } else if (status >= 500) {
            message = `Server đang gặp sự cố (lỗi ${status}). Vui lòng thử lại.`;
        }

        // Caller có thể tự xử lý và hiển thị lỗi theo ngữ cảnh riêng
        if (!err?.config?.skipErrorNotification) {
            ElNotification({
                title: "Lỗi",
                message,
                type: "error",
            });
        }
        err.friendlyMessage = message;
        return Promise.reject(err);
    }
);

export default apiClient;
